/**
 * The dialog an advisor confirms in before their agreement to a full book read is withdrawn.
 *
 * It only renders: what is shown and what happens on confirming come from
 * `useBookReadConsentWithdrawal`, which the field-map page owns. Nothing is withdrawn until the
 * advisor confirms and the withdrawal has been saved.
 */
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@design-system';
import type { useBookReadConsentWithdrawal } from '@client/hooks/useBookReadConsentWithdrawal';

interface BookReadConsentWithdrawalDialogProps {
  crmName: string;
  withdrawal: ReturnType<typeof useBookReadConsentWithdrawal>;
}

export function BookReadConsentWithdrawalDialog({ crmName, withdrawal }: BookReadConsentWithdrawalDialogProps) {
  return (
    <Dialog
      open={withdrawal.isOpen}
      onOpenChange={open => {
        if (!open) withdrawal.cancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Withdraw consent to read your {crmName} book?</DialogTitle>
          <DialogDescription className="text-gray-800">{withdrawal.text}</DialogDescription>
        </DialogHeader>

        {!!withdrawal.error && (
          <p className="text-sm text-red-700">{(withdrawal.error as Error).message}</p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={withdrawal.cancel}
            disabled={withdrawal.isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={withdrawal.confirm}
            disabled={withdrawal.isSaving}
          >
            {withdrawal.isSaving ? 'Saving...' : 'Withdraw consent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
